import { EditorView, keymap } from "@codemirror/view"
import { EditorState } from "@codemirror/state"
import { basicSetup } from "codemirror"
import { syntaxHighlighting, HighlightStyle } from "@codemirror/language"
import { tags } from "@lezer/highlight"
import { indentWithTab } from "@codemirror/commands"
import { poeFilter } from "../poe_filter_language.js"
import { poeFilterAutocomplete } from "../poe_filter_autocomplete.js"
import { poeFilterLinter } from "../poe_filter_linting.js"


const BlockTextArea = {
  mounted() {
    this.initializeCodeMirror();
    
    // Expose saveContent method to be called from LiveView
    this.el.saveContent = () => this.saveContent();
  },

  beforeUpdate() {
    // Store the current editor state before LiveView updates
    if (this.editor) {
      this._preservedState = {
        content: this.editor.state.doc.toString(),
        selection: this.editor.state.selection
      };
    }
  },

  updated() {
    // Check if CodeMirror container still exists in DOM
    const container = this.el.parentNode.querySelector('.codemirror-container');
    const editorExists = this.editor && this.editor.dom && this.editor.dom.parentNode;
    
    if (!container || !editorExists) {
      // Clean up any existing editor
      if (this.editor) {
        try {
          this.editor.destroy();
        } catch (e) {
          // Ignore destroy errors
        }
      }
      
      // Remove any existing container
      const existingContainer = this.el.parentNode.querySelector('.codemirror-container');
      if (existingContainer) {
        existingContainer.remove();
      }
      
      // Force recreation
      this.initializeCodeMirror();
      
      // Restore preserved state if available
      if (this._preservedState) {
        this.editor.dispatch({
          changes: {
            from: 0,
            to: this.editor.state.doc.length,
            insert: this._preservedState.content
          },
          selection: this._preservedState.selection
        });
        this._preservedState = null;
      } else if (this.el.value) {
        // Use the textarea value if no preserved state
        this.editor.dispatch({
          changes: {
            from: 0,
            to: this.editor.state.doc.length,
            insert: this.el.value
          }
        });
      }
      return;
    }
    
    // Skip updates if we're in the middle of a save operation to prevent CodeMirror breaking
    if (this._skipNextUpdate) {
      this._skipNextUpdate = false;
      return;
    }
    
    // Skip updates if the editor is focused (user is actively editing)
    if (this.editor && this.editor.hasFocus) {
      return;
    }
    
    // Only sync content if it's actually different and CodeMirror exists
    if (this.editor && this.el.value && this.editor.state.doc.toString() !== this.el.value) {
      // Check if the difference is significant (not just whitespace)
      const editorContent = this.editor.state.doc.toString().trim();
      const textareaContent = this.el.value.trim();
      
      if (editorContent !== textareaContent) {
        this.editor.dispatch({
          changes: {
            from: 0,
            to: this.editor.state.doc.length,
            insert: this.el.value
          }
        });
      }
    }
  },

  destroyed() {
    if (this.editor) {
      this.editor.destroy();
    }
  },


  // Generate theme-aware color scheme (kept for reference)
  getColorScheme(isDark) {
    if (isDark) {
      return {
        keyword: "#ff7849",     
        propertyName: "#36d399",
        function: "#ff9f1c",     
        atom: "#3abff8",         
        variableName: "#f87272", 
        string: "#ce9178",       
        number: "#6a9955",       
        operator: "#ffb86c",     
        comment: "#737373" 
      };
      
    } else {
      return {
        keyword: "#ef476f",      
        propertyName: "#09620B", 
        function: "#ffd166",     
        atom: "#118ab2",         
        variableName: "#ef476f", 
        string: "#0b6989",       
        number: "#09620B",       
        operator: "#425266",     
        comment: "#6b6b6b"       
      };
    }
  },


  // Generate theme-aware highlight style for CodeMirror 6
  getHighlightStyle(isDark) {
    const colors = this.getColorScheme(isDark);
    
    return HighlightStyle.define([
      // Keywords (Show, Hide, Continue)
      { tag: tags.keyword, color: colors.keyword, fontWeight: "bold" },
      
      // Property names (BaseType, Class, ItemLevel, etc.)
      { tag: tags.propertyName, color: colors.propertyName, fontWeight: "600" },
      
      // Functions/Actions (SetTextColor, SetBackgroundColor, MinimapIcon, etc.)
      { tag: tags.function, color: colors.function, fontWeight: "600" },
      { tag: tags.function(tags.variableName), color: colors.function, fontWeight: "600" },
      { tag: tags.function(tags.propertyName), color: colors.function, fontWeight: "600" },
      { tag: tags.function(tags.string), color: colors.function, fontWeight: "600" },
      { tag: tags.function(tags.number), color: colors.function, fontWeight: "600" },
      { tag: tags.function(tags.atom), color: colors.function, fontWeight: "600" },
      // Try alternative tags that might be used for functions
      { tag: tags.definition(tags.propertyName), color: colors.function, fontWeight: "600" },
      { tag: tags.local(tags.propertyName), color: colors.function, fontWeight: "600" },
      
      // Atoms (True, False)
      { tag: tags.atom, color: colors.atom, fontWeight: "bold" },
      
      // Variable names (Normal, Magic, Rare, Unique)
      { tag: tags.variableName, color: colors.variableName, fontWeight: "bold" },
      
      // Strings (quoted values)
      { tag: tags.string, color: colors.string },
      
      // Numbers (including line numbers)
      { tag: tags.number, color: colors.number },
      
      // Operators (=, !=, <, >, etc.)
      { tag: tags.operator, color: colors.operator, fontWeight: "bold" },
      
      // Comments (# lines)
      { tag: tags.comment, color: colors.comment, fontStyle: "italic" },
      
      // Additional tags for better coverage
      { tag: tags.punctuation, color: colors.operator }
    ]);
  },

  initializeCodeMirror() {
    try {
      // Create editor container
      const editorContainer = document.createElement('div');
      editorContainer.className = 'codemirror-container';
      
      // Check if this is the filter text mode (full width mode)
      const isFilterTextMode = this.el.id === 'filter-text';
      
      if (isFilterTextMode) {
        // For filter text mode, use a larger max-width since it's the main editor
        editorContainer.style.maxWidth = '1200px';
        editorContainer.style.width = '100%';
        editorContainer.style.margin = '0 auto'; // Center the editor
      } else {
        // For regular block/section text areas, use smaller max-width        
        editorContainer.style.width = '100%';
      }
      
      // Insert container after the textarea
      this.el.parentNode.insertBefore(editorContainer, this.el.nextSibling);
      
      // Instead of hiding the textarea, position it behind the CodeMirror editor
      // This allows blur events to still work properly
      this.el.style.position = 'absolute';
      this.el.style.opacity = '0';
      this.el.style.pointerEvents = 'none';
      this.el.style.zIndex = '-1';
    
    // Detect theme based on DaisyUI data-theme attribute
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const isDark = currentTheme === 'halloween';
    const colors = this.getColorScheme(isDark);
    // Detect mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
    
    // Get realm from data attribute
    const realm = this.el.dataset.realm || 'pc';
    
    // Create CodeMirror editor
    const extensions = [      
      basicSetup,
      keymap.of([indentWithTab]),
      poeFilter(),
      poeFilterAutocomplete(realm),
      poeFilterLinter(),             
      syntaxHighlighting(this.getHighlightStyle(isDark)),      
      EditorView.theme({
        "&": {
          fontSize: isMobile ? "16px" : "14px",
          fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
          height: "100%"
        },
        ".cm-content": {
          color: isDark ? "#ffffff" : "#000000", // Default text color based on theme
          padding: isMobile ? "18px" : "12px",
          minHeight: isMobile ? "300px" : "200px",
          textAlign: "left" // Fix text centering issue
        },
        // Current line selection styling
        ".cm-activeLine": {
          backgroundColor: isDark ? "rgba(255, 120, 73, 0.1)" : "rgba(239, 71, 111, 0.1)" // Halloween/Retro theme colors with transparency
        },
        ".cm-activeLineGutter": {
          backgroundColor: isDark ? "rgba(255, 120, 73, 0.2)" : "rgba(239, 71, 111, 0.2)",
          color: isDark ? "#ff7849" : "#ef476f"
        },
        // Line numbers styling
        ".cm-gutters": {
          backgroundColor: isDark ? "#1a1a1a" : "#f8f9fa",
          color: isDark ? "#6a9955" : "#06d6a0",
          borderRight: `1px solid ${isDark ? "#333333" : "#e9ecef"}`
        },
        ".cm-gutterElement": {
          color: isDark ? "#6a9955" : "#06d6a0"
        },
        ".cm-editor": {
          borderRadius: "8px",
          border: "1px solid var(--fallback-bc,oklch(var(--bc)/0.2))",
          width: "100%",
          position: "relative" // Fix positioning issues
        },
        // Autocomplete tooltip styling
        ".cm-tooltip": {
          backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
          border: `1px solid ${isDark ? "#ff7849" : "#ef476f"}`,
          borderRadius: "4px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
        },
        ".cm-tooltip.cm-completionInfo": {
          backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
          border: `1px solid ${isDark ? "#ff7849" : "#ef476f"}`
        },
        ".cm-completionLabel": {
          color: isDark ? "#ffffff" : "#000000"
        },
        ".cm-completionDetail": {
          color: isDark ? "#ff7849" : "#ef476f"
        },
        ".cm-completionMatchedText": {
          color: isDark ? "#36d399" : "#06d6a0",
          fontWeight: "bold"
        },
        // Linting error styling (squiggly lines)
        ".cm-diagnosticError": {
          textDecoration: "underline wavy #f87272", // Halloween error red
          textDecorationSkipInk: "none"
        },
        ".cm-diagnosticWarning": {
          textDecoration: "underline wavy #fbbf24", // Halloween warning yellow
          textDecorationSkipInk: "none"
        },
        ".cm-diagnosticInfo": {
          textDecoration: "underline wavy #3abff8", // Halloween info blue
          textDecorationSkipInk: "none"
        },
        // Cursor styling
        ".cm-cursor": {
          borderLeftColor: isDark ? "#ff7849" : "#ef476f",
          borderLeftWidth: "2px"
        },
        "&.cm-focused .cm-cursor": {
          borderLeftColor: isDark ? "#ff7849" : "#ef476f"
        },
        ".cm-focused": {
          outline: "2px solid var(--fallback-p,oklch(var(--p)/1))",
          outlineOffset: "2px"
        },
        ".cm-scroller": {
          lineHeight: "1.5",
          textAlign: "left" // Ensure left alignment
        },
        // Mobile-specific styles
        ...(isMobile ? {
          ".cm-tooltip": {
            fontSize: "16px"
          },
          ".cm-completionLabel": {
            fontSize: "16px",
            padding: "8px 12px"
          },
          ".cm-diagnostic": {
            fontSize: "14px"
          }
        } : {})
      }),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {          
          // Update the hidden textarea for form submission
          this.el.value = update.state.doc.toString();
          
          // Trigger LiveView events
          this.el.dispatchEvent(new Event('input', { bubbles: true }));
          
          // Check for color picker trigger
          const changes = update.changes;
          changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
            const insertedText = inserted.toString();
            if (insertedText.includes('COLOR_PICKER_TRIGGER')) {
              // Remove the trigger text
              this.editor.dispatch({
                changes: { from: fromB, to: toB, insert: '' }
              });
              
              // Show color picker
              this.showColorPicker(fromB);
            }
          });
        }
      }),

    ];
    
    // Note: We don't need oneDark extension anymore since we're using custom theme-aware colors
    // The oneDark extension was overwriting our custom color scheme
    
    this.editor = new EditorView({
      state: EditorState.create({
        doc: this.el.value,
        extensions: extensions
      }),
      parent: editorContainer
    });
    
    // Listen for theme changes
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'class' || mutation.attributeName === 'data-theme')) {
          this.updateTheme();
        }
      });
    });
    
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });
    
    } catch (error) {
      console.error('CodeMirror initialization failed:', error);
      // Fallback to regular textarea
      this.el.style.display = 'block';
      this.el.style.position = 'static';
      this.el.style.opacity = '1';
      this.el.style.pointerEvents = 'auto';
      this.el.style.zIndex = 'auto';
      // Add some basic styling to make it look better
      this.el.style.fontFamily = 'monospace';
      this.el.style.fontSize = '14px';
      this.el.style.border = '1px solid #ccc';
      this.el.style.borderRadius = '4px';
      this.el.style.padding = '8px';
    }
  },

  updateTheme() {
    // Recreate editor with new theme
    if (this.editor) {
      const currentContent = this.editor.state.doc.toString();
      this.editor.destroy();
      
      // Update the textarea value
      this.el.value = currentContent;
      
      // Reinitialize with new theme
      this.initializeCodeMirror();
    }
  },

  // Legacy method for backward compatibility
  formatContent() {
    if (this.editor) {
      const content = this.editor.state.doc.toString();
      const lines = content.split('\n').map(line => line.trim()).filter(Boolean);
      const formatted = lines.join('\n');
      
      this.editor.dispatch({
        changes: {
          from: 0,
          to: this.editor.state.doc.length,
          insert: formatted
        }
      });
    }
  },

  // Manual save method
  saveContent() {
    if (this.editor) {
      const value = this.editor.state.doc.toString();
      this.el.value = value;
      
      // Set flag to skip the next update to prevent CodeMirror from breaking
      this._skipNextUpdate = true;
      
      // Use pushEvent to send the data directly to the LiveView
      const blockId = this.el.getAttribute('phx-value-block_id');
      const sectionId = this.el.getAttribute('phx-value-section_id');
      
      if (blockId) {
        this.pushEvent("save_block_text", {
          block_id: blockId,
          value: value
        });
      } else if (sectionId) {
        this.pushEvent("save_section_text", {
          section_id: sectionId,
          value: value
        });
      } else {
        // This is the filter text case (no block_id or section_id)
        this.pushEvent("update_filter_text", {
          text: value
        });
      }
    }
  },

  // Color picker functionality
  showColorPicker(cursorPos) {
    
    // Detect current theme
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const isDark = currentTheme === 'halloween';
    
    // Theme-aware colors
    const themeColors = {
      background: isDark ? '#1a1a1a' : '#ffffff',
      text: isDark ? '#ffffff' : '#000000',
      border: isDark ? '#ff7849' : '#ef476f',
      buttonPrimary: isDark ? '#ff7849' : '#ef476f',
      buttonSecondary: isDark ? '#6c757d' : '#6c757d',
      label: isDark ? '#36d399' : '#09620B',
      value: isDark ? '#6a9955' : '#09620B'
    };
    
    // Create color picker container
    const pickerContainer = document.createElement('div');
    pickerContainer.className = 'color-picker-overlay';
    pickerContainer.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: ${themeColors.background};
      color: ${themeColors.text};
      border: 1px solid ${themeColors.border};
      border-radius: 6px;
      padding: 20px;
      z-index: 10000;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      font-family: Arial, sans-serif;
      min-width: 300px;
      max-height: 80vh;
      overflow-y: auto;
    `;
    
    // Create color picker input
    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.value = '#ff0000';
    colorInput.style.cssText = `
      width: 100px;
      height: 40px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      margin-bottom: 15px;
    `;
    
    // Create RGB display
    const rgbDisplay = document.createElement('div');
    rgbDisplay.style.cssText = `
      font-size: 14px;
      margin-bottom: 15px;
      font-family: monospace;
    `;
    
    // Create alpha slider container
    const alphaContainer = document.createElement('div');
    alphaContainer.style.cssText = `
      margin-bottom: 15px;
    `;
    
    const alphaLabel = document.createElement('label');
    alphaLabel.textContent = 'Alpha:';
    alphaLabel.style.cssText = `
      display: block;
      margin-bottom: 5px;
      font-size: 14px;
      font-weight: bold;
      color: ${themeColors.label};
    `;
    
    const alphaSlider = document.createElement('input');
    alphaSlider.type = 'range';
    alphaSlider.min = '0';
    alphaSlider.max = '255';
    alphaSlider.value = '255';
    alphaSlider.style.cssText = `
      width: 100%;
      margin-bottom: 5px;
    `;
    
    const alphaValue = document.createElement('div');
    alphaValue.style.cssText = `
      font-size: 12px;
      color: ${themeColors.value};
      font-family: monospace;
    `;
    
    alphaContainer.appendChild(alphaLabel);
    alphaContainer.appendChild(alphaSlider);
    alphaContainer.appendChild(alphaValue);
    
    // Create buttons
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 20px;
    `;
    
    const applyButton = document.createElement('button');
    applyButton.textContent = 'Apply';
    applyButton.style.cssText = `
      padding: 8px 16px;
      background: ${themeColors.buttonPrimary};
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    `;
    
    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.cssText = `
      padding: 8px 16px;
      background: ${themeColors.buttonSecondary};
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    `;
    
    // Update RGB display when color changes
    const updateRGBDisplay = () => {
      const color = colorInput.value;
      const r = parseInt(color.substr(1,2), 16);
      const g = parseInt(color.substr(3,2), 16);
      const b = parseInt(color.substr(5,2), 16);
      const alpha = alphaSlider.value;
      rgbDisplay.textContent = `RGBA: ${r} ${g} ${b} ${alpha}`;
      alphaValue.textContent = `Alpha value: ${alpha}`;
    };
    
    colorInput.addEventListener('input', updateRGBDisplay);
    alphaSlider.addEventListener('input', updateRGBDisplay);
    updateRGBDisplay();
    
    // Apply color
    applyButton.addEventListener('click', () => {
      const color = colorInput.value;
      const r = parseInt(color.substr(1,2), 16);
      const g = parseInt(color.substr(3,2), 16);
      const b = parseInt(color.substr(5,2), 16);
      const alpha = alphaSlider.value;
      
      // Get current cursor position instead of using the old one
      const currentPos = this.editor.state.selection.main.head;
      
      // Insert RGBA values at current cursor position
      this.editor.dispatch({
        changes: { from: currentPos, to: currentPos, insert: `${r} ${g} ${b} ${alpha}` }
      });
      
      document.body.removeChild(pickerContainer);
    });
    
    // Cancel
    cancelButton.addEventListener('click', () => {
      document.body.removeChild(pickerContainer);
    });
    
    // Prevent closing on outside click - only allow button clicks
    pickerContainer.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    // Prevent any clicks inside the picker from closing it
    pickerContainer.addEventListener('mousedown', (e) => {
      e.stopPropagation();
    });
    
    pickerContainer.addEventListener('mouseup', (e) => {
      e.stopPropagation();
    });
    
    // Assemble and show
    buttonContainer.appendChild(cancelButton);
    buttonContainer.appendChild(applyButton);
    pickerContainer.appendChild(colorInput);
    pickerContainer.appendChild(rgbDisplay);
    pickerContainer.appendChild(alphaContainer);
    pickerContainer.appendChild(buttonContainer);
    document.body.appendChild(pickerContainer);
    
    // Focus the color input
    colorInput.focus();
  }
};

export default BlockTextArea; 